/**
 * AdminGradUrlHealth — small admin board that lists every broken
 * firm grad-scheme URL so we can maintain the seed without hunting
 * through log files.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AlertTriangle, ExternalLink, RefreshCw } from "lucide-react";

export default function AdminGradUrlHealth() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api
      .get("/admin/grad-url-health")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "failed"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="admin-grad-url-health-page">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="label-uppercase text-[#4C1D95]">Admin · Grad-URL health</div>
          <h1 className="mt-2 font-editorial text-3xl sm:text-4xl text-[#002147]">
            Broken firm careers pages
          </h1>
        </div>
        <button
          onClick={load}
          data-testid="admin-grad-url-health-refresh"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-stone-300 hover:border-[#4C1D95] text-sm"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      {err && <div className="rounded-md border border-red-300 bg-red-50 p-4 text-red-800 text-sm">{err}</div>}
      {loading && !data && <div className="text-stone-500 text-sm">loading…</div>}

      {data && (
        <>
          <div className="grid sm:grid-cols-3 gap-3 mb-6">
            <StatCard label="URLs tracked"      value={data.total_urls} />
            <StatCard label="Healthy"           value={`${data.healthy_ratio_pct}%`} accent="text-emerald-700" />
            <StatCard label="Broken (action)"   value={data.broken_count} accent="text-red-700" />
          </div>
          <div className="text-[11px] font-mono text-stone-500 mb-4" data-testid="admin-grad-url-health-last-pass">
            Last full daemon pass: {data.last_pass_at || "not yet run"}
          </div>

          {data.broken.length === 0 ? (
            <div className="rounded-md border border-emerald-300 bg-emerald-50 p-6 text-emerald-900">
              All {data.total_urls} tracked grad-scheme URLs currently return a healthy status.
              The health-check daemon runs weekly — check back Monday.
            </div>
          ) : (
            <div className="rounded-md border border-stone-200 overflow-hidden" data-testid="admin-grad-url-health-broken-table">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-[10px] font-underground-sc tracking-wider text-stone-600">
                  <tr>
                    <th className="p-3 text-left">Country</th>
                    <th className="p-3 text-left">Firm</th>
                    <th className="p-3 text-left">URL (broken)</th>
                    <th className="p-3 text-right">Status</th>
                    <th className="p-3 text-right">Checked</th>
                  </tr>
                </thead>
                <tbody>
                  {data.broken.map((r) => (
                    <tr key={`${r.country}-${r.firm_name}`}
                        className="border-t border-stone-100 hover:bg-red-50"
                        data-testid={`admin-grad-url-health-row-${r.country}-${r.firm_name.replace(/\s+/g,'-')}`}>
                      <td className="p-3 font-mono text-xs">{r.country}</td>
                      <td className="p-3 flex items-center gap-2">
                        <AlertTriangle className="h-3.5 w-3.5 text-red-600" />
                        {r.firm_name}
                      </td>
                      <td className="p-3">
                        <a href={r.grad_url} target="_blank" rel="noreferrer"
                           className="text-[#4C1D95] hover:underline inline-flex items-center gap-1 max-w-md truncate">
                          <span className="truncate">{r.grad_url}</span>
                          <ExternalLink className="h-3 w-3 shrink-0" />
                        </a>
                      </td>
                      <td className="p-3 text-right font-mono font-bold text-red-700">{r.status}</td>
                      <td className="p-3 text-right font-mono text-[11px] text-stone-500">
                        {(r.checked_at || "").slice(0, 10)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-6 rounded-md border border-stone-200 bg-stone-50 p-4">
            <div className="label-uppercase text-[#4C1D95] mb-2">Healthy URLs by country</div>
            <div className="flex flex-wrap gap-3 text-sm">
              {Object.entries(data.healthy_by_country).map(([cc, n]) => (
                <div key={cc} className="px-3 py-1.5 rounded-full bg-white border border-stone-200 font-mono">
                  {cc}: {n}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="rounded-md border border-stone-200 bg-white p-4">
      <div className="text-[10px] font-underground-sc tracking-wider text-stone-500">{label}</div>
      <div className={`mt-1 font-chip font-bold text-2xl ${accent || "text-[#002147]"}`}>{value}</div>
    </div>
  );
}
